// =============================================
// embedUtils — allow-listed video embed parsing + trusted src reconstruction
// =============================================
// An embed is a pure reference (provider + id) — no bytes, nothing user-owned.
// SECURITY: we parse a user-pasted URL down to {provider, videoId} against a strict
// allow-list, store ONLY those, and rebuild the iframe src from a trusted template
// at render time (embedSrc). So even a hand-edited document can never point the
// iframe at an attacker-chosen URL — the worst case is an invalid id → no embed.
// =============================================

export type EmbedProvider = 'youtube' | 'vimeo';
export interface Embed { provider: EmbedProvider; videoId: string; }

const YT_ID = /^[A-Za-z0-9_-]{11}$/;   // YouTube ids are 11 url-safe chars
const VIMEO_ID = /^\d+$/;              // Vimeo ids are numeric

/** Parse a pasted URL to an allow-listed embed, or null if unsupported/invalid. */
export function parseEmbed(raw: string): Embed | null {
  if (!raw) return null;
  let url: URL;
  try { url = new URL(raw.trim()); } catch { return null; }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null; // no javascript:/data:
  const host = url.hostname.replace(/^www\./, '').toLowerCase();

  // ---- YouTube ----
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    const v = url.searchParams.get('v');
    if (v && YT_ID.test(v)) return { provider: 'youtube', videoId: v };
    const m = url.pathname.match(/^\/(?:embed|shorts|v)\/([A-Za-z0-9_-]{11})/);
    if (m) return { provider: 'youtube', videoId: m[1] };
    return null;
  }
  if (host === 'youtu.be') {
    const id = url.pathname.slice(1).split('/')[0];
    if (YT_ID.test(id)) return { provider: 'youtube', videoId: id };
    return null;
  }

  // ---- Vimeo ----
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const parts = url.pathname.split('/').filter(Boolean);
    const id = parts[parts.length - 1];
    if (id && VIMEO_ID.test(id)) return { provider: 'vimeo', videoId: id };
    return null;
  }

  return null;
}

/** Rebuild a privacy-friendly embed src from a TRUSTED template (revalidates the id). */
export function embedSrc(provider: string, videoId: string): string | null {
  if (provider === 'youtube' && YT_ID.test(videoId)) return `https://www.youtube-nocookie.com/embed/${videoId}`;
  if (provider === 'vimeo' && VIMEO_ID.test(videoId)) return `https://player.vimeo.com/video/${videoId}`;
  return null;
}

export function providerLabel(p: string): string {
  return p === 'youtube' ? 'YouTube' : p === 'vimeo' ? 'Vimeo' : 'Video';
}
